import React, { type ReactNode, useState } from 'react';

import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import ToggleOnOutlinedIcon from '@mui/icons-material/ToggleOnOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Card,
  CardContent,
  Divider,
  Stack,
  Typography,
} from '@mui/material';

import { PromoFeatureKey, type PromoFeatureRoleContext } from '@luxuryescapes/contract-svc-promo';

import { usePromoFeaturesConfig } from '~/hooks/usePromoFeatureAvailability';

import isProd from '~/utils/isProd';

import PromoFeatureHeader from './PromoFeatureHeader';
import { findPromoFeatureIndicator } from './PromoTester/promoFeatureIndicators';

export type PromoFeaturePresentation = 'box' | 'accordion' | 'raw';

interface Props {
  /** A contract feature key or an Admin Portal-only feature key. */
  featureKey: PromoFeatureKey;
  /** Defaults to the signed-in user's auth roles when omitted. */
  authRoles?: PromoFeatureRoleContext['authRoles'];
  /** Pass loaded promo roles; omit while their source is unresolved. */
  promoRoles?: PromoFeatureRoleContext['promoRoles'];
  /** `raw` has no outer surface; use it inside an existing card or accordion. */
  presentation?: PromoFeaturePresentation;
  /** Accordion presentations start closed unless explicitly expanded. */
  defaultExpanded?: boolean;
  /** Denied or unresolved access never renders children. Defaults to hiding the feature. */
  accessMode?: 'hide' | 'show-warning';
  fallbackTitle?: string;
  children: ReactNode;
}

type ConfigurationItem = {
  icon: ReactNode;
  label: string;
};

function getConfigurationItems(
  feature: NonNullable<ReturnType<typeof findPromoFeatureIndicator>>,
): ReadonlyArray<ConfigurationItem> {
  const isExperimental = feature.docType !== 'feature';
  const requiredRoleNames = feature.requiredRoles?.roles
    .filter((requirement) => requirement.type === 'auth_role' || requirement.type === 'promo_role')
    .map((requirement) => requirement.key);
  const roleLabel = requiredRoleNames?.join(feature.requiredRoles?.operator === 'and' ? ' and ' : ' or ');

  return [
    ...(roleLabel
      ? [
          {
            icon: <AdminPanelSettingsOutlinedIcon fontSize="small" />,
            label: roleLabel,
          },
        ]
      : []),
    ...(isExperimental
      ? [
          {
            icon: <ScienceOutlinedIcon fontSize="small" />,
            label: 'Experimental',
          },
        ]
      : []),
    ...(feature.isOnlyTest
      ? [
          {
            icon: <VisibilityOffOutlinedIcon fontSize="small" />,
            label: 'Test only',
          },
        ]
      : []),
    ...(feature.canOptIn
      ? [
          {
            icon: <ToggleOnOutlinedIcon fontSize="small" />,
            label: 'Opt-in',
          },
        ]
      : []),
    ...(feature.isFunctionallyComplete
      ? [
          {
            icon: <CheckCircleOutlineIcon fontSize="small" />,
            label: 'Complete',
          },
        ]
      : []),
  ];
}

/** Enforces contract access and environment availability before rendering feature controls. */
export default function PromoFeature({
  featureKey,
  authRoles,
  promoRoles,
  presentation = 'box',
  defaultExpanded = false,
  accessMode = 'hide',
  fallbackTitle,
  children,
}: Props) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const { config } = usePromoFeaturesConfig({ authRoles, promoRoles });
  const feature = findPromoFeatureIndicator(config.features, featureKey);

  const isUnavailableInEnvironment = feature?.isOnlyTest && isProd();

  // Fail closed while roles are unresolved or the installed catalogue lacks this feature.
  if (!feature || feature.canUse !== true || isUnavailableInEnvironment) {
    if (accessMode === 'hide') {
      return null;
    }

    const featureTitle = feature?.displayName ?? fallbackTitle ?? featureKey;
    const warningMessage = !feature
      ? 'Feature access could not be verified.'
      : isUnavailableInEnvironment
        ? 'This feature is only available in the test environment.'
        : feature.canUse === 'unknown'
          ? 'Feature access could not be verified. Required roles may still be loading.'
          : 'You do not meet the access requirements for this feature.';

    return <Alert severity="warning">{`${featureTitle}: ${warningMessage}`}</Alert>;
  }

  const configurationItems = getConfigurationItems(feature);
  const header = <PromoFeatureHeader feature={feature} configurationItems={configurationItems} />;

  if (presentation === 'raw') {
    return (
      <Stack spacing={2}>
        {header}
        {children}
      </Stack>
    );
  }

  if (presentation === 'accordion') {
    return (
      <Accordion
        variant="outlined"
        expanded={isExpanded}
        onChange={(_, expanded) => setIsExpanded(expanded)}
        disableGutters
        sx={{
          borderRadius: 1,
          width: isExpanded ? '100%' : 'fit-content',
          maxWidth: '100%',
          '&:before': { display: 'none' },
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>{header}</AccordionSummary>
        <AccordionDetails sx={{ borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
          <Stack spacing={2}>
            <Stack spacing={0.5}>
              <Typography variant="body2">{feature.description}</Typography>
              <Typography variant="body2" color="text.secondary">
                {feature.currentStatusHeadline}
              </Typography>
              {feature.notes && (
                <Typography variant="body2" color="text.secondary">
                  {feature.notes}
                </Typography>
              )}
            </Stack>
            {children}
          </Stack>
        </AccordionDetails>
      </Accordion>
    );
  }

  return (
    <Card
      variant="outlined"
      sx={(theme) => ({
        width: '100%',
        borderColor: theme.palette.mode === 'dark' ? theme.palette.action.disabled : theme.palette.divider,
      })}
    >
      <CardContent sx={{ '&:last-child': { pb: 2 } }}>
        <Stack spacing={2}>
          {header}
          <Divider />
          {children}
        </Stack>
      </CardContent>
    </Card>
  );
}
